import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticationService } from '@app/services/auth.service';
import { AuthGuard } from './core/guards/auth.guard';
import { AdminAuthGuard } from '@app/guards/adminAuth.guard';
import { RoleGuard } from '@app/services/roleGuard.service';
import { Role } from '@app/services/Roles/roleEnum';

const appRoutes: Routes = [
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
  },
  // {
  //   path: 'dashboard',
  //   loadChildren: () =>
  //     import('./features/dashboard/dashboard.module').then(
  //       (m) => m.DashboardModule
  //     ),
  //   canActivate: [AuthGuard],
  // },
  {
    path: 'lab-master',
    loadChildren: () =>
      import('./features/lab-master/lab-master.module').then(
        (m) => m.LabMasterModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: { expectedRoles: [Role.Admin] },
  },
  {
    path: 'doctors',
    loadChildren: () =>
      import('./features/doctors/doctors.module').then((m) => m.DoctorsModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [
        Role.Admin,
        Role.HospitalManager,
        Role.OperationManager,
        Role.Receptionist,
      ],
    },
  },
  {
    path: 'ipd',
    loadChildren: () =>
      import('./features/ipd/add-ipd-patient/add-ipd-patient.module').then(
        (m) => m.AddIpdPatientModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [
        Role.Admin,
        Role.HospitalManager,
        Role.OperationManager,
        Role.Receptionist,
        Role.Pharmacist,
        Role.PharmacyManager,
        Role.StockTransfer,
        Role.PharmacyOperator,
      ],
    },
  },

  {
    path: 'ipd-payout',
    loadChildren: () =>
      import('./features/ipd-payout/ipd-payout.module').then(
        (m) => m.IpdPayoutModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin, Role.OperationManager],
    },
  },
  {
    path: 'patients',
    loadChildren: () =>
      import('./features/patients/patients.module').then(
        (m) => m.PatientsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [
        Role.Admin,
        Role.HospitalManager,
        Role.OperationManager,
        Role.Receptionist,
      ],
    },
  },
  {
    path: 'staff',
    loadChildren: () =>
      import('./features/receptionist/receptionist.module').then(
        (m) => m.ReceptionistModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin],
    },
  },
  {
    path: 'receptionist',
    loadChildren: () =>
      import('./features/receptionist/receptionist.module').then(
        (m) => m.ReceptionistModule
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'masters',
    loadChildren: () =>
      import('./features/masters/masters.module').then((m) => m.MastersModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'lab-reports',
    loadChildren: () =>
      import('./features/lab-reports/lab-reports.module').then(
        (m) => m.LabReportsModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin],
    },
  },
  {
    path: 'all-prescription',
    loadChildren: () =>
      import('./features/all-prescription/all-prescription.module').then(
        (m) => m.AllPrescriptionModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin],
    },
  },
  {
    path: 'managed-coupon',
    loadChildren: () =>
      import('./features/managed-coupon/managed-coupon.module').then(
        (m) => m.ManagedCouponModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin],
    },
  },
  {
    path: 'managed-clinic',
    loadChildren: () =>
      import('./features/managed-clinic/managed-clinic.module').then(
        (m) => m.ManagedClinicModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin],
    },
  },
  {
    path: 'banner-app',
    loadChildren: () =>
      import('./features/banner-app/banner-app.module').then(
        (m) => m.BannerAppModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin],
    },
  },
  {
    path: 'packages',
    loadChildren: () =>
      import('./features/packages/packages.module').then(
        (m) => m.PackagesModule
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'refund',
    loadChildren: () =>
      import('./features/refund-list/refund-list.module').then(
        (m) => m.RefundModule
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'all-wallet-transaction',
    loadChildren: () =>
      import(
        './features/all-wallet-transaction/all-wallet-transaction.module'
      ).then((m) => m.AllWalletTransactionModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin, Role.OperationManager, Role.HospitalManager],
    },
  },

  {
    path: 'cashflow',
    loadChildren: () =>
      import('./features/cashflow/cashflow.module').then(
        (m) => m.CashflowModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [
        Role.Admin,
        Role.OperationManager,
        Role.HospitalManager,
        Role.Receptionist,
        Role.Pharmacist,
        Role.PharmacyManager,
      ],
    },
  },

  {
    path: 'all-appointment',
    loadChildren: () =>
      import('./features/all-appointment/all-appointment.module').then(
        (m) => m.AllAppointmentModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [
        Role.Admin,
        Role.OperationManager,
        Role.HospitalManager,
        Role.Pharmacist,
        Role.PharmacyManager,
        Role.StockTransfer,
        Role.PharmacyOperator,
        Role.Receptionist,
      ],
    },
  },

  {
    path: 'activity-log',
    loadChildren: () =>
      import('./features/activity-log/activity-log.module').then(
        (m) => m.ActivityLogModule
      ),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [Role.Admin, Role.OperationManager, Role.HospitalManager],
    },
  },

  {
    path: 'messages',
    loadChildren: () =>
      import('./features/messages/whatsapp-messages.module').then(
        (m) => m.whatsappMessageModule
      ),
    canActivate: [AuthGuard, AdminAuthGuard],
  },
  {
    path: 'clinic-list',
    loadChildren: () =>
      import(
        './features/clinic-room-assignment/clinic-room-assignment.module'
      ).then((m) => m.ClinicRoomAssignmentModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'process',
    loadChildren: () =>
      import('./features/process-scheduler/process-scheduler.module').then(
        (m) => m.ProcessSchedulerModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'pharmacy',
    loadChildren: () =>
      import('./features/pharmacy/pharmacy.module').then(
        (m) => m.PharmacyModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'services',
    loadChildren: () =>
      import('./features/service/service.module').then((m) => m.ServiceModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'store',
    loadChildren: () =>
      import('./features/store/store.module').then((m) => m.StoreModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'account',
    loadChildren: () =>
      import('./features/account/account.module').then((m) => m.AccountModule),
    canActivate: [AuthGuard, RoleGuard],
    data: {
      expectedRoles: [
        Role.Doctor,
        Role.Customer,
        Role.Receptionist,
        Role.Watcher,
        Role.HospitalManager,
        Role.OperationManager,
        Role.WhatsappManager,
        Role.PharmacyOperator,
        Role.Pharmacist,
        Role.PharmacyManager,
        Role.StockTransfer,
        Role.Auditor,
        Role.taskReviewer,
        Role.StoreManager,
        Role.IPDPayoutApprover,
        Role.StoreApproval,
      ],
    },
  },
  {
    path: '**',
    redirectTo: 'doctors',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes)],
  exports: [RouterModule],
  providers: [],
})
export class AppRoutingModule {
  constructor(private authService: AuthenticationService) {}
}
